import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package, Plus, Search, Clock, CreditCard, Pencil, Trash2,
  ToggleLeft, ToggleRight, Users, ChevronRight
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useAdmin } from '@/contexts/AdminContext';
import SiteFilter from '@/components/admin/SiteFilter';
import CategoryManager from '@/components/admin/CategoryManager';
import { cn } from '@/lib/utils';

const AUDIENCE_STYLES = {
  patient: 'bg-blue-50 text-blue-700 border-blue-200',
  professional: 'bg-purple-50 text-purple-700 border-purple-200',
};

export default function ServicesIndex() {
  const { apiFetch } = useAdmin();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [siteFilter, setSiteFilter] = useState('all');
  const [deleting, setDeleting] = useState(null);
  const [showCategories, setShowCategories] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const q = siteFilter === 'all' ? '/services?allStatuses=true' : `/services?siteKey=${siteFilter}&allStatuses=true`;
        const res = await apiFetch(q);
        const data = await res.json();
        setServices(data.services || data || []);
      } catch (err) {
        console.error('Failed to fetch services:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [apiFetch, siteFilter]);

  const toggleActive = async (service) => {
    try {
      await apiFetch(`/services/${service.id}`, {
        method: 'PUT',
        body: { isActive: !service.isActive },
      });
      setServices(prev => prev.map(s => s.id === service.id ? { ...s, isActive: !s.isActive } : s));
    } catch (err) {
      console.error('Failed to toggle service:', err);
    }
  };

  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      await apiFetch(`/services/${id}`, { method: 'DELETE' });
      setServices(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      console.error('Failed to delete service:', err);
    } finally {
      setDeleting(null);
    }
  };

  const filtered = services.filter(s => {
    if (!search) return true;
    return s.name?.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-400">Catalogue</p>
          <h2 className="text-2xl lg:text-3xl font-playfair font-medium text-stone-900 tracking-tight mt-1">Services</h2>
          <p className="text-stone-500 text-sm mt-1">Manage your service catalogue, pricing, and provider assignments.</p>
        </div>
        <div className="flex items-center gap-2">
          {siteFilter !== 'all' && (
            <Button variant="outline" className="h-10 gap-2 border-stone-200 text-stone-600 bg-white hover:bg-stone-50" onClick={() => setShowCategories(true)}>
              <Settings className="w-4 h-4" />
              Manage Categories
            </Button>
          )}
          <Link to="/admin/services/new">
            <Button className="h-10 gap-2 bg-stone-900 hover:bg-stone-800 text-white shadow-sm px-5">
              <Plus className="w-4 h-4" /> Add Service
            </Button>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 w-4 h-4" />
          <Input placeholder="Search services..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-white border-stone-200 h-10" />
        </div>
        <SiteFilter value={siteFilter} onChange={setSiteFilter} />
      </div>

      {/* Services Table */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-stone-200">
          <CardContent className="py-16 text-center">
            <Package className="w-12 h-12 text-stone-200 mx-auto mb-4" />
            <p className="text-stone-600 font-medium">No services found</p>
            <p className="text-stone-400 text-sm mt-1">
              {search ? 'Try a different search term' : 'Create your first service to get started'}
            </p>
            {!search && (
              <Link to="/admin/services/new">
                <Button variant="outline" className="mt-4 gap-2"><Plus className="w-4 h-4" /> Add Service</Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="border-stone-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-100 bg-stone-50/50">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Service</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider hidden md:table-cell">Audience</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider hidden sm:table-cell">Duration</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Deposit</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Status</th>
                  <th className="px-5 py-3 w-24"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filtered.map((service, i) => (
                  <motion.tr
                    key={service.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.03 }}
                    className={cn(
                      'hover:bg-stone-50/50 transition-colors',
                      !service.isActive && 'opacity-50'
                    )}
                  >
                    <td className="px-5 py-3.5">
                      <p className="font-medium text-stone-900">{service.name}</p>
                      {service.shortDescription && (
                        <p className="text-xs text-stone-400 mt-0.5 line-clamp-1">{service.shortDescription}</p>
                      )}
                    </td>
                    <td className="px-5 py-3.5 hidden md:table-cell">
                      <Badge variant="outline" className={cn('text-[10px] capitalize', AUDIENCE_STYLES[service.audience] || 'bg-stone-100 text-stone-600 border-stone-200')}>
                        {service.audience || 'Patient'}
                      </Badge>
                    </td>
                    <td className="px-5 py-3.5 text-center hidden sm:table-cell">
                      <span className="text-xs text-stone-600">{service.duration || '—'} min</span>
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <span className="font-semibold text-stone-900">£{service.depositAmount || 0}</span>
                    </td>
                    <td className="px-5 py-3.5 text-center">
                      <button onClick={() => toggleActive(service)} className="inline-flex items-center gap-1">
                        {service.isActive ? (
                          <ToggleRight className="w-5 h-5 text-emerald-500" />
                        ) : (
                          <ToggleLeft className="w-5 h-5 text-stone-300" />
                        )}
                      </button>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-1 justify-end">
                        <Link to={`/admin/services/${service.id}/edit`}>
                          <button className="h-8 w-8 rounded-lg hover:bg-stone-100 flex items-center justify-center transition-colors">
                            <Pencil className="w-3.5 h-3.5 text-stone-500" />
                          </button>
                        </Link>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button className="h-8 w-8 rounded-lg hover:bg-red-50 flex items-center justify-center transition-colors">
                              <Trash2 className="w-3.5 h-3.5 text-stone-400 hover:text-red-500" />
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete "{service.name}"?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will soft-delete this service. Existing bookings will not be affected.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(service.id)} className="bg-red-600 hover:bg-red-700">
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <AnimatePresence>
        {showCategories && (
          <CategoryManager 
            siteKey={siteFilter} 
            onClose={() => setShowCategories(false)} 
          />
        )}
      </AnimatePresence>
    </div>
  );
}
